
import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';

interface CameraDevice {
  deviceId: string;
  label: string;
}

export function useCameraHandler() {
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isListingDevices, setIsListingDevices] = useState(false);
  const [hasCameraSupport, setHasCameraSupport] = useState(true);
  const [activeCamera, setActiveCamera] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  // Énumérer les dispositifs de caméra
  const enumerateDevices = async () => {
    setIsListingDevices(true);
    setError(null);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        setHasCameraSupport(false);
        setError('Votre navigateur ne supporte pas l\'accès à la caméra');
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices
        .filter(device => device.kind === 'videoinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Caméra ${device.deviceId.substring(0, 4)}`
        }));

      setDevices(videoDevices);
      
      if (videoDevices.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(videoDevices[0].deviceId);
      }

      if (videoDevices.length === 0) {
        setError('Aucune caméra détectée sur cet appareil');
        setHasCameraSupport(false);
      }

    } catch (err) {
      console.error('Erreur lors de l\'énumération des dispositifs:', err);
      setError('Impossible d\'accéder aux dispositifs de caméra');
      setHasCameraSupport(false);
    } finally {
      setIsListingDevices(false);
    }
  };

  // Démarrer la caméra
  const startCamera = async (deviceId?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Arrêter le stream existant s'il y en a un
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }

      const constraints: MediaStreamConstraints = {
        video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode }
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(newStream);
      setActiveCamera(true);
      
      if (deviceId) {
        setSelectedDeviceId(deviceId);
      }

    } catch (err) {
      console.error('Erreur lors du démarrage de la caméra:', err);
      setError('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
      setActiveCamera(false);
      toast.error('Impossible d\'accéder à la caméra');
    } finally {
      setIsLoading(false);
    }
  };

  // Arrêter la caméra
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setActiveCamera(false);
  };

  // Changer de caméra
  const switchCamera = async (deviceId: string) => {
    if (activeCamera) {
      await startCamera(deviceId);
    } else {
      setSelectedDeviceId(deviceId);
    }
  };

  // Basculer entre caméras (avant/arrière)
  const toggleCamera = () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    if (activeCamera) {
      startCamera();
    }
  };

  // Énumérer les dispositifs au montage
  useEffect(() => {
    enumerateDevices();
  }, []);

  // Nettoyer lors du démontage
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return {
    devices,
    selectedDeviceId,
    stream,
    error,
    isLoading,
    isListingDevices,
    hasCameraSupport,
    activeCamera,
    facingMode,
    startCamera,
    stopCamera,
    switchCamera,
    toggleCamera,
    retry: enumerateDevices
  };
}
